import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  FaCheckCircle, 
  FaAward, 
  FaUsers, 
  FaGlobe, 
  FaMapMarkerAlt, 
  FaRocket,
  FaStar,
  FaCertificate,
  FaCloudUploadAlt,
  FaShieldAlt,
  FaLightbulb,
  FaArrowRight,
  FaPlay,
  FaTrophy,
  FaCogs,
  FaCode,
  FaReact,
  FaNodeJs,
  FaPython,
  FaJava,
  FaAws,
  FaDatabase,
  FaMobile,
  FaPaintBrush,
  FaDocker
} from 'react-icons/fa';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

// Import images directly
import awsImage from '../assets/images/aws-in-rajasthan-shanav-1728034787.webp';
import gcpImage from '../assets/images/google-cloud-platform-in-rajasthan-shanav-1728034805.webp';
import azureImage from '../assets/images/microsoft-azure-in-rajasthan-shanav-1728034816.webp';
import cloudComputingImage from '../assets/images/aboutimg.jpg';
import TeamCard from '../components/ui/TeamCard';
import { fadeIn, staggerContainer } from '../utils/animations';
import LeaderProfile from '../components/ui/LeaderProfile';
import { leadership, teamMembers } from '../data/teamData';

const CountUp = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      onViewportEnter={() => setIsVisible(true)}
      className="text-center"
    >
      <motion.div
        className="text-4xl md:text-5xl font-bold text-white mb-2"
        animate={{ scale: isVisible ? [1, 1.1, 1] : 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        {isVisible ? count : 0}{suffix}
      </motion.div>
    </motion.div>
  );
};



const FloatingOrb = ({ className, delay = 0, duration = 4 }) => (
  <motion.div
    className={`absolute rounded-full blur-xl ${className}`}
    animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.8, 0.3],
      x: [-10, 10, -10],
      y: [-5, 5, -5]
    }}
    transition={{
      duration,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }}
  />
);

const AboutPage = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const achievements = [
    { value: 45, suffix: '+', label: 'Cloud Experts', icon: FaUsers, color: 'from-blue-500 to-cyan-500' },
    { value: contentData.company.experience_years, suffix: '+', label: 'Years Experience', icon: FaGlobe, color: 'from-purple-500 to-pink-500' },
    { value: parseInt(contentData.company.client_satisfaction), suffix: '%', label: 'Client Satisfaction', icon: FaAward, color: 'from-green-500 to-teal-500' },
    { value: parseInt(contentData.company.projects_completed), suffix: '+', label: 'Projects Completed', icon: FaCheckCircle, color: 'from-orange-500 to-red-500' }
  ];

  // SEO data for About page
  const seoData = {
    title: "About Us - Cloud Technology Experts",
    description: `Learn about ${contentData.company.name} - ${contentData.company.tagline}. ${contentData.company.mission} With ${contentData.company.experience_years}+ years of experience and ${contentData.company.client_satisfaction} client satisfaction.`,
    keywords: "About Dhyon Technologies, Cloud Experts, AWS Partners, Cloud Consulting Team, Cloud Migration Specialists, DevOps Experts"
  };

  const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
        duration: 1,
      staggerChildren: 0.2
    }
  }
};

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };




  const timeline = [
    { year: '2021', title: 'Company Founded', description: 'Started with a vision to transform businesses through cloud technology.' },
    { year: '2022', title: 'AWS Partnership', description: 'Established a strategic partnership with Amazon Web Services.' },
    { year: '2023', title: 'Industry Recognition', description: 'Acknowledged for excellence in cloud services and digital transformation.' },
    { year: '2024', title: 'AWS Select Tier Partner', description: 'Achieved AWS Select Tier Partner status, validating our technical proficiency.', badge: '/assets/select tier badge.png' },
    { year: '2025', title: 'AWS Advanced Tier Partner & UAE Expansion', description: 'Reached AWS Advanced Tier Partner status and expanded our global footprint with a new office in the UAE.', badge: '/assets/AWS-Partner-Advanced-Tier-Services 1 (1) (1).jpg' },
    { year: '2026', title: 'Advancing Global Software & Startup Ecosystems', description: 'Empowering global innovation through advanced software engineering, cloud excellence, and startup enablement.' }
  ];

  return (
    <>
      <SEO {...seoData} />
      
      {/* Hero Section - Completely Redesigned */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <FloatingOrb className="top-20 left-20 w-96 h-96 bg-blue-500/20" delay={0} />
          <FloatingOrb className="top-40 right-10 w-80 h-80 bg-purple-500/20" delay={2} />
          <FloatingOrb className="bottom-20 left-1/3 w-72 h-72 bg-cyan-500/20" delay={4} />
          
          {/* Particle Effects */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.2
              }}
            />
          ))}
        </div>

        {/* Glowing Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <motion.div 
          className="container relative z-10 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-8"
            whileHover={{ scale: 1.05 }}
          >
            <FaRocket className="text-blue-400 mr-2" />
            <span className="text-white font-medium">About {contentData.company.name}</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1 
            variants={itemVariants}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight uppercase"
          >
            <span className="block text-white mb-2">Empowering Businesses</span>
            <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-gradient-x">
              Through Technology Excellence
            </span>
          </motion.h1>

          <motion.h2 
            variants={itemVariants}
            className="text-2xl md:text-3xl text-blue-200 font-semibold mb-8 max-w-4xl mx-auto"
          >
            Driving scalable growth with AWS expertise and intelligent software solutions.
          </motion.h2>

          <motion.p 
            variants={itemVariants}
            className="text-xl md:text-2xl text-blue-100/90 max-w-4xl mx-auto mb-12 leading-relaxed"
          >
              Dhyon is an AWS Advanced Tier Partner delivering high-performance cloud and software solutions. 
              We help startups and enterprises build, scale, and innovate with secure, future-ready technology 
              that accelerates growth and operational efficiency.
          </motion.p>

        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Core Expertise Section - Added per request */}
      <section className="py-24 bg-white relative z-20 -mt-10 mx-4 md:mx-0">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <FaCloudUploadAlt className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AWS Advanced Tier Partner</h3>
              <p className="text-gray-600 leading-relaxed">
                Delivering secure, scalable, and high-performance cloud solutions powered by AWS expertise to help businesses modernize and grow with confidence.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <FaCogs className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Software Architecture & Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Building robust, future-ready software solutions using modern technologies to drive efficiency, scalability, and long-term business value.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <FaRocket className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Startup Enablement</h3>
              <p className="text-gray-600 leading-relaxed">
                Empowering startups to transform ideas into market-ready products through strategic guidance, agile development, and scalable technology.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Mission Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container relative z-10">
          <div className="flex flex-col items-center gap-12">
            <motion.div 
              className="w-full max-w-4xl mx-auto text-center space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <center>
                <motion.div
                  className="inline-flex items-center bg-blue-50 border border-blue-100 rounded-full px-6 py-2 shadow-sm mb-4"
                  whileHover={{ y: -2 }}
                >
                  <FaRocket className="text-blue-600 mr-2" />
                  <span className="text-blue-700 font-bold text-sm tracking-wide uppercase">Our Mission</span>
                </motion.div>
              </center>
              
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
                Shaping the Future of <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Digital Innovation</span>
              </h2>
            </motion.div>

            <motion.div 
              className="w-full max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative group max-w-2xl mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-[40px] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
                <div className="relative rounded-[32px] overflow-hidden shadow-2xl">
                  <img 
                    src="/assets/mission.png" 
                    alt="Our Mission" 
                    className="w-full h-auto transform transition-transform duration-1000 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="w-full max-w-4xl mx-auto text-center space-y-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="space-y-6 max-w-3xl mx-auto">
                <p className="text-xl text-slate-600 leading-relaxed">
                  Our mission is to shape the future of digital innovation by enabling enterprises and startups to think bigger, build smarter, and scale faster. As an AWS Advanced Tier Partner, we go beyond implementation—driving meaningful transformation through intelligent software, cloud-native architectures, and forward-looking technology strategies.
                </p>
                <p className="text-xl text-slate-600 leading-relaxed">
                  We believe true progress comes from combining deep technical expertise with strategic insight. By simplifying complexity and anticipating what’s next, we empower organizations to create resilient systems, unlock new opportunities, and lead with confidence in an ever-evolving digital world.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Vision Section */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="container relative z-10">
          <div className="flex flex-col items-center gap-12">
            <motion.div 
              className="w-full max-w-4xl mx-auto text-center space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <center>
                <motion.div
                  className="inline-flex items-center bg-purple-50 border border-purple-100 rounded-full px-6 py-2 shadow-sm mb-4"
                  whileHover={{ y: -2 }}
                >
                  <FaUsers className="text-purple-600 mr-2" />
                  <span className="text-purple-700 font-bold text-sm tracking-wide uppercase">Leadership Approach</span>
                </motion.div>
              </center>
              
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
                Lead with Clarity, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Purpose & Accountability</span>
              </h2>
            </motion.div>

            <motion.div 
              className="w-full max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative group max-w-2xl mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-[40px] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
                <div className="relative rounded-[32px] overflow-hidden shadow-2xl">
                  <img 
                    src="/assets/leadership.png" 
                    alt="Our Leadership" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="w-full max-w-4xl mx-auto text-center space-y-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <p className="text-xl text-slate-600 leading-relaxed italic border-l-4 border-purple-500 pl-6 max-w-3xl mx-auto">
                "We lead with clarity, purpose, and accountability. At Dhyon, leadership means anticipating change, empowering innovation, and enabling teams to deliver meaningful impact. By aligning technology, strategy, and people, we drive sustainable growth and help organizations navigate the future with confidence."
              </p>
            </motion.div>
          </div>
        </div>
      </section>






      {/* Cloud Technology Partners - Centered version */}
      <section className="py-20 bg-gray-50">
        <div className="container overflow-hidden">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h5 className="text-blue-600 font-medium mb-3 uppercase tracking-wider">Our Expertise</h5>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-5">Cloud Technology Partners</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We are certified partners with leading cloud providers, offering specialized services for multiple platforms.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                img: awsImage, 
                title: 'Amazon Web Services', 
                desc: 'Delivering secure, scalable, and high-performance cloud solutions powered by AWS expertise to help businesses modernize and grow with confidence.',
                link: '/services/aws'
              },
              { 
                img: gcpImage, 
                title: 'Google Cloud', 
                desc: 'Leveraging Google Cloud to drive innovation with advanced data analytics, AI/ML capabilities, and scalable infrastructure solutions.'
              },
              { 
                img: azureImage, 
                title: 'Microsoft Azure', 
                desc: 'Building robust enterprise-grade solutions on Azure with seamless integration, security, and hybrid cloud capabilities.'
              }
            ].map((partner, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group w-full"
              >
                <div className="flex justify-center items-center h-24 mb-6">
                  <img 
                    src={partner.img} 
                    alt={partner.title} 
                    className="h-20 max-w-full object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-center text-gray-800">{partner.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed mb-8 min-h-[80px]">
                  {partner.desc}
                </p>
                <div className="text-center">
                  {partner.link && (
                    <Link to={partner.link} className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700">
                      Learn more <FaArrowRight className="ml-2" />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section Refined */}
      <section className="py-20 md:py-28 bg-white relative z-10">
        <div className="container">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h5 className="text-primary font-medium mb-3 uppercase tracking-wider">Visionaries</h5>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-5">Our Leadership</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Guiding our innovative journey with expertise and passion.
            </p>
          </motion.div>

          {/* Leadership Section */}
          <div className="max-w-5xl mx-auto mb-24">
            {leadership.map((leader, index) => (
              <LeaderProfile key={leader.id} leader={leader} index={index} />
            ))}
          </div>

          {/* Team Divider */}
          <div className="flex items-center justify-center mb-20 opacity-50">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-full max-w-2xl"></div>
          </div>

          {/* Team Grid */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4">Meet the Team</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              The talented individuals powering our success.
            </p>
          </motion.div>
          
          <motion.div 
            className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 place-items-center sm:place-items-stretch"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {teamMembers.map((member, index) => (
              <TeamCard key={member.id} member={member} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <FloatingOrb className="top-20 left-20 w-96 h-96 bg-blue-500/20" delay={0} />
          <FloatingOrb className="bottom-20 right-20 w-80 h-80 bg-purple-500/20" delay={2} />
          
          {/* Animated Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:80px_80px]" />
        </div>

        <motion.div 
          className="container relative z-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-8"
              whileHover={{ scale: 1.05 }}
            >
              <FaRocket className="text-blue-400 mr-2" />
              <span className="text-white font-medium">Let's Connect</span>
            </motion.div>
            
            <h2 className="mb-8 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="text-white">Ready to Transform</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Your Business?
              </span>
            </h2>
            
            <p className="mb-12 text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Join the hundreds of businesses that have transformed their operations with our cloud solutions.
            </p>
            
            <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
              <Link 
                to="/contact" 
                className="group relative inline-flex items-center justify-center px-10 py-5 bg-white text-blue-900 font-bold rounded-2xl shadow-2xl hover:shadow-white/25 transition-all duration-300 transform hover:-translate-y-2 overflow-hidden text-lg"
              >
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative flex items-center group-hover:text-white transition-colors duration-300">
                  <FaRocket className="mr-3 transform group-hover:rotate-12 transition-transform duration-200" />
                Contact Us Today
                  <FaArrowRight className="ml-3 transform group-hover:translate-x-1 transition-transform duration-200" />
                </span>
              </Link>
              

            </div>
          </div>
        </motion.div>
      </section>

      {/* Company Journey Timeline - New Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <FloatingOrb className="top-20 right-20 w-64 h-64 bg-blue-500/10" delay={1} />
          <FloatingOrb className="bottom-40 left-10 w-80 h-80 bg-purple-500/10" delay={3} />
        </div>

        <div className="container relative z-10">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <FaCertificate className="text-blue-400 mr-2" />
              <span className="text-white font-medium">Our Journey</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-white">Our Path to</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Excellence
              </span>
            </h2>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              From humble beginnings to industry leadership - our journey of innovation and growth.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-400 via-purple-400 to-cyan-400 rounded-full"></div>

            <div className="space-y-16">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <motion.div
                      className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-white/20 transition-all duration-300"
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      {item.badge && (
                        <div className={`flex ${index % 2 === 0 ? 'justify-end' : 'justify-start'} mb-6`}>
                          <img 
                            src={item.badge} 
                            alt={item.title} 
                            className="h-24 w-auto object-contain bg-white/20 rounded-xl p-3 backdrop-blur-md border border-white/20 shadow-lg"
                          />
                        </div>
                      )}
                      <div className="text-4xl font-bold text-blue-400 mb-4">{item.year}</div>
                      <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                      <p className="text-blue-100 leading-relaxed">{item.description}</p>
                    </motion.div>
                  </div>
                  
                  {/* Timeline Node */}
                  <div className="relative z-10">
                    <motion.div
                      className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full border-4 border-white shadow-lg"
                      animate={{
                        scale: [1, 1.2, 1],
                        boxShadow: ['0 0 0 0 rgba(59, 130, 246, 0.7)', '0 0 0 10px rgba(59, 130, 246, 0)', '0 0 0 0 rgba(59, 130, 246, 0)']
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.5
                      }}
                    />
                  </div>
                  
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>





      {/* Tech Expertise Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <FloatingOrb className="top-10 left-10 w-72 h-72 bg-blue-500/10" delay={0} />
          <FloatingOrb className="bottom-20 right-20 w-64 h-64 bg-purple-500/10" delay={2} />
          <FloatingOrb className="top-1/2 left-1/2 w-80 h-80 bg-cyan-500/10" delay={4} />
        </div>

        <div className="container relative z-10">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <FaCode className="text-blue-400 mr-2" />
              <span className="text-white font-medium">Our Stack</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-white">Tech Stack</span>
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Oriented
              </span>
            </h2>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              We leverage a powerful combination of frontend and backend technologies to build scalable, high-performance solutions.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 lg:grid-cols-5 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {[
              { 
                icon: FaReact, 
                title: 'Frontend Dev', 
                desc: 'React, Angular, Vue, Next.js' 
              },
              { 
                icon: FaNodeJs, 
                title: 'Backend Dev', 
                desc: 'Node.js, Express, NestJS' 
              },
              { 
                icon: FaMobile, 
                title: 'Mobile Apps', 
                desc: 'React Native, Flutter, iOS' 
              },
              { 
                icon: FaPaintBrush, 
                title: 'UI/UX Design', 
                desc: 'Figma, Adobe XD, Prototyping' 
              },
              { 
                icon: FaCheckCircle, 
                title: 'QA & Testing', 
                desc: 'Manual, Automation, Selenium' 
              },
              { 
                icon: FaPython, 
                title: 'AI & ML', 
                desc: 'Python, TensorFlow, PyTorch' 
              },
              { 
                icon: FaJava, 
                title: 'Enterprise Java', 
                desc: 'Spring Boot, Microservices' 
              },
              { 
                icon: FaAws, 
                title: 'Cloud Services', 
                desc: 'Amazon Web Services' 
              },
              { 
                icon: FaDocker, 
                title: 'DevOps', 
                desc: 'Docker, K8s, CI/CD, Terraform' 
              },
              { 
                icon: FaDatabase, 
                title: 'Databases', 
                desc: 'SQL, MongoDB, PostgreSQL' 
              }
            ].map((tech, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="group relative"
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                
                <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all duration-300 h-full flex flex-col items-center text-center">
                  <motion.div 
                    className="flex justify-center items-center h-14 w-14 mb-4 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 group-hover:border-blue-400/50 transition-colors"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <tech.icon className="text-3xl text-blue-300 group-hover:text-white transition-colors duration-300" />
                  </motion.div>
                  
                  <h3 className="text-lg font-bold mb-2 text-white group-hover:text-blue-200 transition-colors duration-300">
                    {tech.title}
                  </h3>
                  
                  <p className="text-blue-100/80 text-sm leading-snug">
                    {tech.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Enhanced Locations Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white to-blue-50/50 relative overflow-hidden">
        <div className="container">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-200/50 rounded-full px-6 py-3 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <FaGlobe className="text-blue-600 mr-2" />
              <span className="text-gray-700 font-medium">Global Presence</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">Our</span>
              <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                Locations
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              With offices strategically located around the world, we provide our clients with local expertise and global capabilities.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {contentData.company.locations.map((location, index) => (
              <motion.div 
                key={index} 
                variants={itemVariants}
                className="group relative"
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                
                <div className="relative bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-10 shadow-lg group-hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center mb-8">
                    <motion.div 
                      className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mr-6 group-hover:scale-110 transition-transform duration-300"
                      animate={{
                        rotate: [0, 5, 0, -5, 0]
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 1
                      }}
                    >
                      <FaMapMarkerAlt className="text-2xl text-white" />
                    </motion.div>
                    <h3 className="text-3xl font-bold text-gray-900">{location.city}</h3>
                  </div>
                  
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">{location.address}</p>
                  
                  <Link 
                    to={location.mapUrl || `https://maps.google.com/?q=${encodeURIComponent(location.address)}`}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="group/link inline-flex items-center text-blue-600 hover:text-blue-500 font-semibold transition-colors duration-200"
                  >
                    View on Map 
                    <FaArrowRight className="ml-2 transform group-hover/link:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage; 